import './App.css';
import StartupCalculator from "./components/StartupCalculator";
import EncryptionTester from "./components/EncryptionTester";
import DatabaseViewer from "./components/DatabaseViewer";
import LoginScreen from "./components/LoginScreen";
import LocalizationProvider from './context/LocalizationProvider';
import { NotificationProvider } from './context/NotificationContext';
import { useState, useEffect } from 'react';
import db from './utils/database';
import useLocalization from './hooks/useLocalization';
import { verifyPassword } from './utils/encryption';
import { FullScreenLoading } from './components/common/LoadingIndicator';
import { handleDatabaseError } from './utils/errorHandler';

// Main App wrapper - this component doesn't use notifications
function App() {
  return (
    <NotificationProvider>
      <LocalizationProvider>
        <AppContent />
      </LocalizationProvider>
    </NotificationProvider>
  );
}

// Inner AppContent component - this one can use the notification context
function AppContent() {
  const translations = useLocalization();
  const { showSuccess, showError, showInfo, showWarning } = require('./context/NotificationContext').useNotification();
  
  const [activeView, setActiveView] = useState('calculator'); // 'calculator', 'encryption', 'database'
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [globalError, setGlobalError] = useState(null);

  // Login handler with secure password verification and error handling
  const handleLogin = async (enteredPassword) => {
    setIsVerifying(true);
    setGlobalError(null);

    try {
      // Verify against the hashed password
      const isValid = await verifyPassword(enteredPassword);

      if (isValid) {
        setPassword(enteredPassword);
        setIsAuthenticated(true);
        showSuccess('Login successful! Welcome to the application.');

        // Set the password in the database utility for later use
        db.setPassword(enteredPassword);

        // Pre-connect to database in the background for better UX
        setGlobalLoading(true);
        try {
          await db.getClient(enteredPassword);
          // Success will be handled silently
        } catch (dbError) {
          // Database connection errors won't block login, but will be shown later
          const errorMessage = handleDatabaseError(dbError, 'background connection');
          console.warn('Background database connection failed:', dbError);
          showWarning(`Database connection warning: ${errorMessage}. You can retry connecting from the Database Viewer.`);
        } finally {
          setGlobalLoading(false);
        }
      } else {
        const errorMsg = translations.loginScreen?.incorrectPasswordMessage || "Incorrect password!";
        setGlobalError(errorMsg);
        showError(errorMsg);
      }
    } catch (error) {
      console.error('Password verification error:', error);
      const errorMessage = handleDatabaseError(error, 'authentication') ||
        'An error occurred during authentication. Please try again.';
      setGlobalError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsVerifying(false);
    }
  };

  // Only show content when authenticated
  if (!isAuthenticated) {
    return (
      <div className="App">
        {globalLoading && <FullScreenLoading message="Connecting to Database..." />}

        <LoginScreen
          onLogin={handleLogin}
          isVerifying={isVerifying}
          error={globalError}
          loginScreenInfo={translations.loginScreen || {
            title: "Startup Success Calculator",
            subtitle: "Please enter the password to continue",
            passwordPlaceholder: "Enter password",
            loginButtonText: "Login",
            incorrectPasswordMessage: "Incorrect password!"
          }}
        />
      </div>
    );
  }

  // Display main content after authentication
  return (
    <div className="App">
      {globalLoading && <FullScreenLoading message="Processing request..." />}

      <div className="flex flex-col h-screen">
        <div className="p-2 bg-gray-100 flex flex-wrap gap-2 items-center shadow-md z-10">
          <button
            onClick={() => {
              setActiveView('calculator');
              showInfo('Switched to Startup Calculator');
            }}
            className={`px-4 py-1.5 rounded ${activeView === 'calculator' ? 'bg-purple-600 text-white' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'}`}
          >
            Startup Calculator
          </button>
          <button
            onClick={() => {
              setActiveView('encryption');
              showInfo('Switched to Encryption Tester');
            }}
            className={`px-4 py-1.5 rounded ${activeView === 'encryption' ? 'bg-purple-600 text-white' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'}`}
          >
            Encryption Tester
          </button>
          <button
            onClick={() => {
              setActiveView('database');
              showInfo('Switched to Database Viewer');
            }}
            className={`px-4 py-1.5 rounded ${activeView === 'database' ? 'bg-purple-600 text-white' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'}`}
          >
            Database Viewer
          </button>

          <div className="ml-auto text-xs sm:text-sm bg-green-100 text-green-800 px-2 py-1 rounded flex items-center">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
            Authenticated
          </div>
        </div>

        <div className={`flex-grow relative ${activeView === 'database' ? 'overflow-hidden' : 'overflow-auto'}`}>
          {activeView === 'calculator' && <div className="p-4"><StartupCalculator isAuthenticated={true} /></div>}
          {activeView === 'encryption' && <div className="p-4"><EncryptionTester password={password} /></div>}
          {activeView === 'database' && <div className="absolute inset-0"><DatabaseViewer password={password} /></div>}
        </div>
      </div>

      {globalError && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg" role="alert">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm">{globalError}</p>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  onClick={() => setGlobalError(null)}
                  className="inline-flex rounded-md p-1.5 text-red-500 hover:bg-red-200 focus:outline-none"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;